import React from 'react';

/**
 * PipelineBar — visual stage progress indicator for the thinking workflow.
 * Shows where the user is in the Generate → Critique → Refine → Evolve → Export pipeline.
 * Stages adapt per mode.
 */

const PIPELINES = {
  idea:     ['Generate', 'Critique',  'Refine', 'Evolve', 'Export'],
  codebase: ['Generate', 'Audit',     'Refine', 'Prototype', 'Export'],
  decision: ['Generate', 'Debate',    'Compare','Refine', 'Export'],
  writing:  ['Generate', 'Editorial', 'Refine', 'Export'],
  plan:     ['Generate', 'Risk',      'Refine', 'Evolve', 'Export'],
  learn:    ['Generate', 'Quiz',      'Gaps',   'Deepen', 'Master'],
  resume:   ['Generate', 'Review',    'Refine', 'Export'],
};

export function getPipelineStages(mode) {
  return PIPELINES[mode] || PIPELINES.idea;
}

/**
 * Determine which stages are completed based on session state.
 */
export function computeStageStatus(mode, { nodeCount, debateCount, refineCount, evolveStarted, exported, portfolioCount, isDebating, isRefining, isEvolving }) {
  const stages = getPipelineStages(mode);
  const status = {};

  for (let i = 0; i < stages.length; i++) {
    const stage = stages[i];
    const s = stage.toLowerCase();
    if (s === 'generate') {
      status[stage] = nodeCount > 0 ? 'done' : 'pending';
    } else if (['critique', 'audit', 'debate', 'editorial', 'risk', 'review', 'quiz'].includes(s)) {
      status[stage] = isDebating ? 'active' : debateCount > 0 ? 'done' : (nodeCount > 0 ? 'available' : 'pending');
    } else if (['refine', 'compare', 'gaps'].includes(s)) {
      status[stage] = isRefining ? 'active' : refineCount > 0 ? 'done' : (debateCount > 0 || nodeCount > 0 ? 'available' : 'pending');
    } else if (['evolve', 'deepen', 'prototype'].includes(s)) {
      status[stage] = isEvolving ? 'active' : evolveStarted ? 'done' : (refineCount > 0 || nodeCount > 0 ? 'available' : 'pending');
    } else if (['export', 'master'].includes(s)) {
      status[stage] = exported ? 'done' : (nodeCount > 0 ? 'available' : 'pending');
    } else {
      status[stage] = 'pending';
    }
  }

  return status;
}

/**
 * Find the current active stage — the first non-done stage that is available.
 */
export function getCurrentStage(stages, stageStatus) {
  for (const s of stages) {
    if (stageStatus[s] === 'available') return s;
  }
  // All done or all pending — return first pending
  for (const s of stages) {
    if (stageStatus[s] === 'pending') return s;
  }
  return stages[stages.length - 1];
}

export default function PipelineBar({ mode, stageStatus, onStageClick }) {
  const stages = getPipelineStages(mode);
  const currentStage = getCurrentStage(stages, stageStatus);

  return (
    <div className="pipeline-bar" role="navigation" aria-label="Thinking pipeline">
      {stages.map((stage, i) => {
        const status = stageStatus[stage] || 'pending';
        const isCurrent = stage === currentStage && status !== 'done';
        const isActive = status === 'active';
        return (
          <React.Fragment key={stage}>
            {i > 0 && <span className={`pipeline-connector ${status === 'done' || stageStatus[stages[i - 1]] === 'done' ? 'done' : ''}`} />}
            <button
              className={`pipeline-stage ${status}${isCurrent ? ' current' : ''}${isActive ? ' active' : ''}`}
              onClick={() => onStageClick?.(stage)}
              title={`${stage}${status === 'done' ? ' ✓' : isActive ? ' — running' : status === 'available' ? ' — ready' : ''}`}
              disabled={status === 'pending'}
            >
              <span className="pipeline-dot">
                {status === 'done' ? '✓' : isActive ? '●' : isCurrent ? '●' : '○'}
              </span>
              <span className="pipeline-label">{stage}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
